import React, { useId } from 'react';

export interface AttachedPhoto {
    id: string;
    file: File;
    fileName: string;
    previewUrl: string;
}

interface PhotoAttachFieldProps {
    photos: AttachedPhoto[];
    onChange: (photos: AttachedPhoto[]) => void;
    helperText?: string;
}

const PhotoAttachField: React.FC<PhotoAttachFieldProps> = ({ photos, onChange, helperText }) => {
    const cameraInputId = useId();

    const addFiles = (files: File[]) => {
        const images = files.filter(file => file.type.startsWith('image/'));
        if (images.length === 0) return;
        const next = images.map(file => ({
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            file,
            fileName: file.name,
            previewUrl: URL.createObjectURL(file),
        }));
        onChange([...photos, ...next]);
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(event.target.files || []);
        addFiles(selected);
        event.target.value = '';
    };

    const removePhoto = (id: string) => {
        const target = photos.find(photo => photo.id === id);
        if (target) URL.revokeObjectURL(target.previewUrl);
        onChange(photos.filter(photo => photo.id !== id));
    };

    return (
        <div style={{ marginTop: 16, marginBottom: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Attach photos</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                {helperText || 'Take photo opens the camera.'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <label
                    htmlFor={cameraInputId}
                    className="bs-btn bs-btn-primary"
                    style={{ backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', margin: 0 }}
                >
                    Take photo
                </label>
                <input
                    id={cameraInputId}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handleFileInput}
                />
            </div>

            {photos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
                    {photos.map(photo => (
                        <div key={photo.id} style={{ width: 96 }}>
                            <div style={{ position: 'relative', width: 96, height: 96, borderRadius: 8, overflow: 'hidden', border: '1px solid #cbd5e1', background: '#fff' }}>
                                <img src={photo.previewUrl} alt={photo.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <button
                                    type="button"
                                    onClick={() => removePhoto(photo.id)}
                                    title="Remove photo"
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: 'rgba(15, 23, 42, 0.75)',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        lineHeight: '22px',
                                        padding: 0,
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <div style={{ fontSize: 10, color: '#475569', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={photo.fileName}>
                                {photo.fileName}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PhotoAttachField;
